import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera } from 'types/camera';
import useCamAdVastTag from 'hooks/useCamAdVastTag';
import useVideoJSAds from 'hooks/useVideoJSAds';
import { oneSecond } from 'utils/time';

interface UseReelPlayerAdsProps {
  camera: Camera;
  playerPlaying: boolean;
  currentReelIndex: number;
  shouldCountAsClipPlayed: boolean;
  shouldResetClipsPlayedCount: boolean;
  prerollEnabled?: boolean;
}

interface UseReelPlayerAdsReturn {
  adRunning: boolean;
  adSettings: {
    adVastTagUrl?: string;
  };
  videoAdPluginsLoaded: boolean;
  onVideoJsGoogleImaLoaded: () => void;
  onVideoJsGoogleImaError: (e: any) => void;
  setPlayAd: (playAd: () => void) => void;
  setInitAdContainer: (initAdContainer: () => void) => void;
  setAdRunning: (running: boolean) => void;
  shouldShowAdPlayer: boolean;
  currentTimerMs: number;
  // True once ad infra has answered yes/no — UI can drop the loading state.
  prerollResolved: boolean;
  // True when the pre-roll won't fire (timeout, error, or flag off) — UI may autoplay.
  prerollSkipped: boolean;
  // Calls adDisplayContainer.initialize() — invoke from a click handler so user activation reaches IMA.
  initAdContainer: () => void;
}

const PREROLL_LOAD_TIMEOUT_MS = 10 * oneSecond;
const PREROLL_INFRA_TIMEOUT_MS = 15 * oneSecond;

export const useReelPlayerAds = ({
  camera,
  playerPlaying,
  currentReelIndex,
  shouldCountAsClipPlayed,
  shouldResetClipsPlayedCount,
  prerollEnabled = false,
}: UseReelPlayerAdsProps): UseReelPlayerAdsReturn => {
  const [adRunning, setAdRunningState] = useState(false);
  const [currentTimerMs, setCurrentTimerMs] = useState(0);
  // Reactive signal so the pre-roll effect re-runs when CamAdPlayer mounts late.
  const [playAdReady, setPlayAdReady] = useState(false);
  // Skip the pre-roll if ad infra hasn't loaded — don't strand the user on a loading spinner.
  const [prerollTimedOut, setPrerollTimedOut] = useState(false);
  const numberOfReelsPlayedRef = useRef<number>(-1);
  // Lets the increment effect detect a wrap-around (N>0 → 0) so mid-roll cadence resets on replay/cam-change.
  const previousReelIndexRef = useRef<number>(-1);
  const playAdRef = useRef<() => void>(() => {});
  const initAdContainerRef = useRef<() => void>(() => {});
  const playerPlayTimeRef = useRef<number>(0);
  const lastAdTimeRef = useRef<number>(0);
  const prerollFiredRef = useRef<boolean>(false);

  const {
    isVideoAdPluginsError,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    videoAdPluginsLoaded,
  } = useVideoJSAds();

  const { adVastUrl, adTagLoading } = useCamAdVastTag(false, camera?._id);
  const shouldShowAdPlayer = !isVideoAdPluginsError && !adTagLoading;
  const adSettings = {
    adVastTagUrl: adVastUrl || undefined,
  };

  const setAdRunning = useCallback((running: boolean) => {
    setAdRunningState(running);

    if (!running) {
      lastAdTimeRef.current = Date.now();
      playerPlayTimeRef.current = 0;
      setCurrentTimerMs(0);
    }
  }, []);

  const setPlayAd = useCallback((playAd: () => void) => {
    playAdRef.current = playAd;
    setPlayAdReady(true);
  }, []);

  const setInitAdContainer = useCallback((initAdContainer: () => void) => {
    initAdContainerRef.current = initAdContainer;
  }, []);

  // Stable wrapper so consumers always invoke the latest registered initializer.
  const initAdContainer = useCallback(() => {
    initAdContainerRef.current();
  }, []);

  useEffect(() => {
    // Treat any return-to-0 as a session restart (replay, cam-switch, or nav back) and reset cadence.
    if (previousReelIndexRef.current > 0 && currentReelIndex === 0) {
      numberOfReelsPlayedRef.current = -1;
    }
    if (shouldCountAsClipPlayed) {
      numberOfReelsPlayedRef.current += 1;
    } else if (shouldResetClipsPlayedCount) {
      numberOfReelsPlayedRef.current = -1;
    }
    previousReelIndexRef.current = currentReelIndex;
  }, [currentReelIndex, shouldCountAsClipPlayed, shouldResetClipsPlayedCount]);

  // Gate readiness on adVastUrl — without it, playAd() falls through to the ad-blocker path and silently no-ops.
  const prerollAdInfraReady =
    videoAdPluginsLoaded && playAdReady && !isVideoAdPluginsError && !!adVastUrl;
  // Treat a settled-but-empty SheMedia tag as a skip — no point waiting on the 15s infra fallback.
  const prerollSkipped =
    !prerollEnabled || isVideoAdPluginsError || prerollTimedOut || (!adTagLoading && !adVastUrl);
  const prerollResolved = prerollAdInfraReady || prerollSkipped;

  // Fire the pre-roll on first play per cam; the every-2-clips counter takes over after.
  useEffect(() => {
    const canFirePreroll =
      prerollEnabled &&
      playerPlaying &&
      !prerollFiredRef.current &&
      !adRunning &&
      !isVideoAdPluginsError &&
      videoAdPluginsLoaded &&
      playAdReady &&
      !!adVastUrl &&
      !prerollTimedOut;

    if (canFirePreroll) {
      prerollFiredRef.current = true;
      playAdRef.current();
    }
  }, [
    prerollEnabled,
    playerPlaying,
    adRunning,
    isVideoAdPluginsError,
    videoAdPluginsLoaded,
    playAdReady,
    adVastUrl,
    prerollTimedOut,
  ]);

  // 15s infra-failure fallback: covers ad-blockers that abort the IMA script silently and never trigger onError.
  useEffect(() => {
    if (!prerollEnabled || prerollTimedOut || prerollAdInfraReady || isVideoAdPluginsError) {
      return undefined;
    }
    const handle = window.setTimeout(() => {
      setPrerollTimedOut(true);
    }, PREROLL_INFRA_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [prerollEnabled, prerollTimedOut, prerollAdInfraReady, isVideoAdPluginsError, camera?._id]);

  // 10s give-up timer once the user has clicked play — bails out of the loading spinner.
  useEffect(() => {
    if (!prerollEnabled || prerollTimedOut || !playerPlaying) return undefined;
    const handle = window.setTimeout(() => {
      setPrerollTimedOut(true);
    }, PREROLL_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [prerollEnabled, prerollTimedOut, playerPlaying, camera?._id]);

  useEffect(() => {
    if (isVideoAdPluginsError || adRunning || !playerPlaying) {
      return;
    }

    if (numberOfReelsPlayedRef.current === 2 && !adRunning) {
      if (playAdRef.current && videoAdPluginsLoaded) {
        playAdRef.current();
        numberOfReelsPlayedRef.current = 0;
      }
    }
  }, [
    isVideoAdPluginsError,
    adRunning,
    playerPlaying,
    videoAdPluginsLoaded,
    adVastUrl,
    currentReelIndex,
  ]);

  useEffect(() => {
    lastAdTimeRef.current = Date.now();
    playerPlayTimeRef.current = 0;
    setCurrentTimerMs(0);
    // Re-arm pre-roll on cam change. The midroll counter is reset by the wrap-around branch above.
    prerollFiredRef.current = false;
    setPlayAdReady(false);
    setPrerollTimedOut(false);
  }, [camera?._id]);

  return {
    adRunning,
    adSettings,
    videoAdPluginsLoaded,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    setPlayAd,
    setInitAdContainer,
    setAdRunning,
    shouldShowAdPlayer,
    currentTimerMs,
    prerollResolved,
    prerollSkipped,
    initAdContainer,
  };
};

export default useReelPlayerAds;
